import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import {
  type ComponentProps,
  type ComponentPropsWithoutRef,
  type ElementRef,
  forwardRef,
} from 'react';

import { composeVariants, cva, cx } from '../../utils/cva';
import { useFocusVisible } from '../../utils/use-focus-visible';
import { dataFocusRing } from '../FocusVisible';

const INDICATOR_SVG = (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    xmlns="http://www.w3.org/2000/svg"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    stroke="currentColor"
    fill="none"
  >
    <title>Checkbox Indicator</title>
    <path
      className={cx('invisible', 'group-data-[state=checked]/checkbox:visible')}
      d="M5 8.25L7.25 10.5L11.5 6"
    />
    <path
      className={cx(
        'invisible',
        'group-data-[state=indeterminate]/checkbox:visible',
      )}
      d="M10.5 8L6 8"
    />
  </svg>
);

const checkboxRootVariants = composeVariants(
  dataFocusRing,
  cva({
    base: [
      'group/checkbox',
      'inline-block',
      'm-1',
      'h-4',
      'w-4',
      'rounded-sm',
      'shrink-0',
      'bg-default',
      'text-surface-default',
      'ring-1.5',
      'ring-inset',
      'ring-content-default',
      'bg-default',
      'hover:bg-default-hover',
      'data-[state=checked]:bg-state-selected',
      'data-[state=checked]:ring-transparent',
      'data-[state=checked]:hover:bg-state-selected-hover',
      'data-[state=indeterminate]:bg-state-selected',
      'data-[state=indeterminate]:ring-transparent',
      'data-[state=indeterminate]:hover:bg-state-selected-hover',
      'disabled:!ring-1.5',
      'disabled:!ring-content-state-disabled',
      'disabled:!text-state-disabled',
      'disabled:!bg-default',
      'data-[disabled]:!ring-1.5',
      'data-[disabled]:!ring-content-state-disabled',
      'data-[disabled]:!text-state-disabled',
      'data-[disabled]:!bg-default',
    ],
  }),
);

const checkboxIndicatorVariants = cva({
  base: ['flex', 'items-center', 'justify-center'],
});

export interface CheckboxProps
  extends ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root> {
  indicatorProps?: ComponentProps<typeof CheckboxPrimitive.Indicator>;
  'data-testid'?: string;
}

export const Checkbox = forwardRef<
  ElementRef<typeof CheckboxPrimitive.Root>,
  CheckboxProps
>((props, ref) => {
  const {
    className,
    indicatorProps = {},
    onFocus,
    onBlur,
    'data-testid': dataTestId = 'checkbox',
    ...rest
  } = props;

  const { className: indicatorClassName, ...indicatorRest } = indicatorProps;

  return (
    <CheckboxPrimitive.Root
      ref={ref}
      data-testid={`${dataTestId}-root`}
      className={checkboxRootVariants({ className })}
      {...useFocusVisible({ onFocus, onBlur })}
      {...rest}
    >
      <CheckboxPrimitive.Indicator
        data-testid={`${dataTestId}-indicator`}
        className={checkboxIndicatorVariants({ className: indicatorClassName })}
        {...indicatorRest}
      >
        {INDICATOR_SVG}
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );
});

const dummyCheckboxVariants = composeVariants(
  checkboxRootVariants,
  cva({
    base: [
      'pointer-events-none',
      'group-hover/checkbox:bg-default-hover',
      'group-data-[state=checked]/checkbox:bg-state-selected',
      'group-data-[state=checked]/checkbox:ring-transparent',
      'group-data-[state=checked]:group-hover/checkbox:bg-state-selected-hover',
      'group-data-[state=indeterminate]/checkbox:bg-state-selected',
      'group-data-[state=indeterminate]/checkbox:ring-transparent',
      'group-data-[state=indeterminate]:group-hover/checkbox:bg-state-selected-hover',
      'group-disabled/checkbox:!ring-1.5',
      'group-disabled/checkbox:!ring-content-state-disabled',
      'group-disabled/checkbox:!text-state-disabled',
      'group-disabled/checkbox:!bg-default',
      'group-data-[disabled]/checkbox:!ring-1.5',
      'group-data-[disabled]/checkbox:!ring-content-state-disabled',
      'group-data-[disabled]/checkbox:!text-state-disabled',
      'group-data-[disabled]/checkbox:!bg-default',
    ],
  }),
);

export interface DummyCheckboxProps extends ComponentPropsWithoutRef<'span'> {
  indicatorProps?: ComponentProps<'span'> & { 'data-testid'?: string };
  'data-testid'?: string;
}

export const DummyCheckbox = forwardRef<ElementRef<'span'>, DummyCheckboxProps>(
  (props, ref) => {
    const {
      className,
      indicatorProps = {},
      'data-testid': dataTestId = 'checkbox',
      ...rest
    } = props;

    const {
      className: indicatorClassName,
      'data-testid': indicatorDataTestId = `${dataTestId}-indicator`,
      ...indicatorRest
    } = indicatorProps;

    return (
      <span
        ref={ref}
        data-testid={dataTestId}
        className={dummyCheckboxVariants({ className })}
        {...rest}
      >
        <span
          data-testid={indicatorDataTestId}
          className={checkboxIndicatorVariants({
            className: indicatorClassName,
          })}
          {...indicatorRest}
        >
          {INDICATOR_SVG}
        </span>
      </span>
    );
  },
);
